import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Landmark, AlertCircle, Check, Loader2 } from "lucide-react";
import { getBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount } from "@/lib/api-financial";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  panelAccentDot,
  panelControl,
  panelEmpty,
  panelFieldLabel,
  panelPill,
  panelRow,
  panelSectionHeader,
  panelSectionTitle,
  panelSurface,
} from "@/lib/panel-ui";

// ─── Helpers ────────────────────────────────────────────────────────────────

const BANKS = [
  { code: "001", name: "Banco do Brasil", short: "BB" },
  { code: "033", name: "Santander", short: "SAN" },
  { code: "077", name: "Inter", short: "INTER" },
  { code: "104", name: "Caixa Econômica", short: "CAIXA" },
  { code: "237", name: "Bradesco", short: "BRA" },
  { code: "260", name: "Nubank", short: "NU" },
  { code: "341", name: "Itaú", short: "ITAÚ" },
  { code: "336", name: "C6 Bank", short: "C6" },
  { code: "748", name: "Sicredi", short: "SIC" },
  { code: "756", name: "Sicoob", short: "SIB" },
  { code: "422", name: "Safra", short: "SAF" },
  { code: "655", name: "Neon", short: "NEON" },
  { code: "212", name: "Banco Original", short: "ORI" },
  { code: "380", name: "PicPay Bank", short: "PIC" },
  { code: "290", name: "PagBank", short: "PAG" },
];

const ACCOUNT_TYPES = [
  { value: "corrente", labelKey: "accountTypes.corrente" },
  { value: "poupanca", labelKey: "accountTypes.poupanca" },
  { value: "pagamento", labelKey: "accountTypes.pagamento" },
];

const PIX_TYPES = [
  { value: "cpf", labelKey: "pixTypes.cpf" },
  { value: "email", labelKey: "pixTypes.email" },
  { value: "phone", labelKey: "pixTypes.phone" },
  { value: "random", labelKey: "pixTypes.random" },
];

function getBankShort(bankName: string | null | undefined): string {
  if (!bankName) return "BANK";
  const found = BANKS.find(
    (b) =>
      bankName.includes(b.code) ||
      bankName.toLowerCase().includes(b.name.toLowerCase())
  );
  if (found) return found.short;
  const matchCode = bankName.match(/^\d+/);
  return matchCode ? matchCode[0] : bankName.slice(0, 4).toUpperCase();
}

function formatDocument(doc: string): string {
  const d = doc.replace(/\D/g, "");
  if (d.length <= 11) {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

function maskDocument(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

// ─── Formulário em 2 Colunas (Sem Scroll no Desktop) ────────────────────────

const EMPTY_FORM = {
  account_name: "",
  account_holder_name: "",
  document: "",
  bank_name: "",
  agency: "",
  account_number: "",
  account_type: "corrente",
  pix_key: "",
  pix_key_type: "cpf",
  is_default: false,
};

function AccountForm({
  open,
  onClose,
  onSubmit,
  isPending,
  isOrg,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: typeof EMPTY_FORM) => void;
  isPending: boolean;
  isOrg: boolean;
}) {
  const { t } = useTranslation("producerFinancial");
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const set = (key: keyof typeof EMPTY_FORM, val: string | boolean) =>
    setForm((p) => ({ ...p, [key]: val }));

  const handleClose = () => {
    setForm({ ...EMPTY_FORM });
    onClose();
  };

  const canSubmit =
    form.account_name.trim().length > 0 &&
    form.account_holder_name.trim().length > 0 &&
    form.document.replace(/\D/g, "").length >= 11;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl sm:max-w-3xl rounded-[28px] border border-border/70 bg-white dark:bg-[#18181b] p-0 overflow-hidden shadow-2xl">
        {/* Header Apple Sheet */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-border/50">
          <div className="flex items-center gap-2">
            <span className={panelAccentDot} />
            <DialogTitle className="font-display text-base sm:text-lg font-semibold tracking-tight text-foreground">
              {t("bankAccounts.form.title")}
            </DialogTitle>
          </div>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">
            {isOrg
              ? t("bankAccounts.form.descOrg")
              : t("bankAccounts.form.descPersonal")}
          </DialogDescription>
        </div>

        {/* 2-Column Responsive Body */}
        <div className="p-4 sm:p-7 max-h-[calc(85vh-130px)] overflow-y-auto sm:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-start">
            {/* Coluna 1: Identificação & Titular */}
            <div className="space-y-4">
              <div className="pb-1 border-b border-border/40">
                <p className="text-xs font-semibold text-foreground">Identificação do Titular</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Dados cadastrais do proprietário da conta
                </p>
              </div>

              <div>
                <Label htmlFor="account_name" className={panelFieldLabel}>
                  {t("bankAccounts.form.accountAlias")} <span className="text-primary">*</span>
                </Label>
                <Input
                  id="account_name"
                  placeholder={t("bankAccounts.form.accountAliasPlaceholder")}
                  value={form.account_name}
                  onChange={(e) => set("account_name", e.target.value)}
                  className={panelControl}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t("bankAccounts.form.accountAliasHelper")}
                </p>
              </div>

              <div>
                <Label htmlFor="account_holder_name" className={panelFieldLabel}>
                  {isOrg ? t("bankAccounts.form.holderNameOrg") : t("bankAccounts.form.holderNamePersonal")}{" "}
                  <span className="text-primary">*</span>
                </Label>
                <Input
                  id="account_holder_name"
                  placeholder={
                    isOrg
                      ? t("bankAccounts.form.holderNameOrgPlaceholder")
                      : t("bankAccounts.form.holderNamePersonalPlaceholder")
                  }
                  value={form.account_holder_name}
                  onChange={(e) => set("account_holder_name", e.target.value)}
                  className={panelControl}
                />
              </div>

              <div>
                <Label htmlFor="document" className={panelFieldLabel}>
                  {isOrg ? t("bankAccounts.form.documentOrg") : t("bankAccounts.form.documentPersonal")}{" "}
                  <span className="text-primary">*</span>
                </Label>
                <Input
                  id="document"
                  placeholder={isOrg ? "00.000.000/0001-00" : "000.000.000-00"}
                  value={form.document}
                  onChange={(e) => set("document", maskDocument(e.target.value))}
                  className={cn(panelControl, "tabular-nums")}
                  inputMode="numeric"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t("bankAccounts.form.documentHelper")}
                </p>
              </div>
            </div>

            {/* Coluna 2: Dados Bancários & PIX */}
            <div className="space-y-4">
              <div className="pb-1 border-b border-border/40">
                <p className="text-xs font-semibold text-foreground">Dados Bancários & PIX</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Informações para recebimento de TED ou PIX
                </p>
              </div>

              <div>
                <Label htmlFor="bank_name" className={panelFieldLabel}>{t("bankAccounts.form.bank")}</Label>
                <Select
                  value={form.bank_name}
                  onValueChange={(v) => set("bank_name", v)}
                >
                  <SelectTrigger id="bank_name" className={panelControl}>
                    <SelectValue placeholder={t("bankAccounts.form.selectBank")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {BANKS.map((b) => (
                      <SelectItem key={b.code} value={`${b.code} - ${b.name}`}>
                        {b.code} — {b.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="outro">{t("bankAccounts.form.otherBank")}</SelectItem>
                  </SelectContent>
                </Select>
                {form.bank_name === "outro" && (
                  <Input
                    placeholder={t("bankAccounts.form.otherBankPlaceholder")}
                    className={cn(panelControl, "mt-2")}
                    onChange={(e) => set("bank_name", e.target.value)}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="agency" className={panelFieldLabel}>{t("bankAccounts.form.agency")}</Label>
                  <Input
                    id="agency"
                    placeholder={t("bankAccounts.form.agencyPlaceholder")}
                    value={form.agency}
                    onChange={(e) => set("agency", e.target.value)}
                    className={cn(panelControl, "tabular-nums")}
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <Label htmlFor="account_number" className={panelFieldLabel}>{t("bankAccounts.form.accountNumber")}</Label>
                  <Input
                    id="account_number"
                    placeholder={t("bankAccounts.form.accountNumberPlaceholder")}
                    value={form.account_number}
                    onChange={(e) => set("account_number", e.target.value)}
                    className={cn(panelControl, "tabular-nums")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pix_key_type" className={panelFieldLabel}>Tipo da Chave PIX</Label>
                  <Select
                    value={form.pix_key_type}
                    onValueChange={(v) => set("pix_key_type", v)}
                  >
                    <SelectTrigger id="pix_key_type" className={panelControl}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIX_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {t(`bankAccounts.${type.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pix_key" className={panelFieldLabel}>
                    Chave PIX <span className="text-muted-foreground text-[10px] font-normal">(opcional)</span>
                  </Label>
                  <Input
                    id="pix_key"
                    placeholder={
                      form.pix_key_type === "cpf"
                        ? "000.000.000-00"
                        : form.pix_key_type === "email"
                        ? "contato@empresa.com"
                        : form.pix_key_type === "phone"
                        ? "+55 11 99999-9999"
                        : t("bankAccounts.form.pixKeyPlaceholderRandom")
                    }
                    value={form.pix_key}
                    onChange={(e) => set("pix_key", e.target.value)}
                    className={panelControl}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Aviso campos obrigatórios se incompleto */}
          {!canSubmit && (form.account_name || form.account_holder_name || form.document) && (
            <div className="mt-4 flex items-center gap-2 rounded-2xl bg-amber-500/10 px-4 py-2.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{t("bankAccounts.form.requiredFieldsWarning")}</span>
            </div>
          )}
        </div>

        {/* Footer Apple Sheet */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-t border-border/50 bg-secondary/20 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            {t("bankAccounts.form.footerSecurityNote")}
          </p>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full h-11 sm:h-10 px-4 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors w-full sm:w-auto active:scale-[0.98]"
            >
              {t("bankAccounts.form.cancel")}
            </button>
            <button
              type="button"
              onClick={() => onSubmit(form)}
              disabled={!canSubmit || isPending}
              className={cn(
                panelPill,
                "h-12 sm:h-10 px-5 text-sm sm:text-xs font-semibold bg-primary text-primary-foreground transition-all w-full sm:w-auto",
                "disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2 active:scale-[0.97]"
              )}
            >
              {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {isPending ? t("bankAccounts.form.saving") : t("bankAccounts.form.saveAccount")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Card de Conta Bancária (Estilo Apple Wallet / Passbook) ─────────────────

function AccountCard({
  acc,
  onSetDefault,
  onDelete,
  isSettingDefault,
  isDeleting,
}: {
  acc: any;
  onSetDefault: () => void;
  onDelete: () => void;
  isSettingDefault: boolean;
  isDeleting: boolean;
}) {
  const { t } = useTranslation("producerFinancial");
  
  const accountTypeObj = ACCOUNT_TYPES.find((t) => t.value === acc.account_type);
  const accountTypeLabel = accountTypeObj ? t(`bankAccounts.${accountTypeObj.labelKey}`) : null;
  
  const pixTypeObj = PIX_TYPES.find((t) => t.value === acc.pix_key_type);
  const pixTypeLabel = pixTypeObj ? t(`bankAccounts.${pixTypeObj.labelKey}`) : null;
  const bankMonogram = getBankShort(acc.bank_name);

  return (
    <div className={cn(panelRow, "p-5 sm:p-6 transition-all duration-150 hover:border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4")}>
      <div className="flex items-start sm:items-center gap-4 min-w-0 flex-1">
        {/* Monograma / Insígnia Apple-like do Banco */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary/80 border border-border/50 font-display text-xs font-bold tracking-tight text-foreground shadow-xs">
          {bankMonogram}
        </div>

        <div className="space-y-1 min-w-0 flex-1">
          {/* Header da Conta */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-semibold text-sm sm:text-base text-foreground leading-snug truncate">
              {acc.account_name}
            </p>
            {acc.is_default && (
              <Badge
                variant="secondary"
                className="rounded-full px-2.5 py-0 text-[10px] font-normal text-muted-foreground bg-secondary/80"
              >
                Principal
              </Badge>
            )}
          </div>

          {/* Dados do Banco e Agência */}
          <p className="text-xs text-muted-foreground font-mono">
            <span className="text-foreground/90 font-medium font-sans">{acc.bank_name || "Banco"}</span>
            {accountTypeLabel && (
              <span className="font-sans text-muted-foreground/70"> · {accountTypeLabel}</span>
            )}
            {acc.agency && (
              <span> · Ag. {acc.agency}</span>
            )}
            {acc.account_number && (
              <span> · Conta {acc.account_number}</span>
            )}
          </p>

          {/* Titular e Documento */}
          {(acc.account_holder_name || acc.document) && (
            <p className="text-[11px] text-muted-foreground/80">
              {acc.account_holder_name}
              {acc.document && ` · ${formatDocument(acc.document)}`}
            </p>
          )}

          {/* Chave PIX se cadastrada */}
          {acc.pix_key && (
            <p className="text-[11px] text-muted-foreground/70 pt-0.5">
              PIX ({pixTypeLabel}): <span className="text-foreground/80 font-mono">{acc.pix_key}</span>
            </p>
          )}
        </div>
      </div>

      {/* Ações à direita */}
      <div className="flex items-center justify-end gap-2 shrink-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-border/40 w-full sm:w-auto">
        {!acc.is_default && (
          <button
            type="button"
            onClick={onSetDefault}
            disabled={isSettingDefault}
            className="rounded-full h-10 sm:h-8 px-3.5 sm:px-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/70 border border-border/60 sm:border-transparent transition-all active:scale-[0.97] flex items-center gap-1.5 min-h-[40px] sm:min-h-0"
            title={t("bankAccounts.card.setDefaultTooltip")}
          >
            {isSettingDefault ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Tornar principal
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border/60 sm:border-transparent transition-colors min-h-[40px] sm:min-h-0 active:scale-[0.96]"
          title="Excluir conta"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Componente Principal ───────────────────────────────────────────────────

export default function ProducerBankAccounts({
  producerId,
  organizationId,
}: {
  producerId: string;
  organizationId?: string;
}) {
  const { t } = useTranslation("producerFinancial");
  const queryClient = useQueryClient();
  const isOrg = !!organizationId;
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const queryKey = ["bank-accounts", isOrg ? `org:${organizationId}` : producerId];

  const { data: accounts = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getBankAccounts(isOrg ? null : producerId, organizationId),
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: (form: typeof EMPTY_FORM) =>
      createBankAccount(
        isOrg
          ? {
              organization_id: organizationId,
              created_by: producerId,
              ...form,
              document: form.document.replace(/\D/g, ""),
            }
          : {
              producer_id: producerId,
              ...form,
              document: form.document.replace(/\D/g, ""),
            }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowCreate(false);
      toast({ title: t("bankAccounts.toast.addSuccess") });
    },
    onError: (err: any) =>
      toast({ title: t("bankAccounts.toast.addError"), description: err.message, variant: "destructive" }),
  });

  const setDefaultMut = useMutation({
    mutationFn: async (id: string) => {
      setSettingDefaultId(id);
      for (const acc of (accounts as any[])) {
        if (acc.is_default && acc.id !== id) {
          await updateBankAccount(acc.id, { is_default: false });
        }
      }
      await updateBankAccount(id, { is_default: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSettingDefaultId(null);
      toast({ title: t("bankAccounts.toast.defaultUpdateSuccess") });
    },
    onError: (err: any) => {
      setSettingDefaultId(null);
      toast({ title: t("bankAccounts.toast.error"), description: err.message, variant: "destructive" });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      setDeletingId(id);
      await deleteBankAccount(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setDeletingId(null);
      toast({ title: t("bankAccounts.toast.removeSuccess") });
    },
    onError: (err: any) => {
      setDeletingId(null);
      toast({ title: t("bankAccounts.toast.error"), description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-[24px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header com estilo refinado Apple / Panel-UI */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className={cn(panelSectionTitle, "flex items-center gap-2")}>
            <span className={panelAccentDot} />
            {isOrg ? t("bankAccounts.header.titleOrg") : t("bankAccounts.header.titlePersonal")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isOrg
              ? t("bankAccounts.header.descOrg")
              : t("bankAccounts.header.descPersonal")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className={cn(
            panelPill,
            "h-10 px-4 rounded-full text-xs font-semibold bg-foreground text-background hover:bg-foreground/90 transition-all flex items-center justify-center gap-1.5 shrink-0"
          )}
        >
          <Plus className="h-3.5 w-3.5" />
          <span>{t("bankAccounts.header.newAccount")}</span>
        </button>
      </div>

      {(accounts as any[]).length === 0 ? (
        <div className={cn(panelEmpty, "py-12 px-6 text-center space-y-3")}>
          <div className="flex h-12 w-12 mx-auto items-center justify-center rounded-2xl bg-secondary/80 text-muted-foreground">
            <Landmark className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{t("bankAccounts.emptyState.title")}</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
              {t("bankAccounts.emptyState.description")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className={cn(
              panelPill,
              "mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-medium bg-secondary text-foreground hover:bg-secondary/80 transition-all"
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Cadastrar primeira conta</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {(accounts as any[]).map((acc) => (
            <AccountCard
              key={acc.id}
              acc={acc}
              onSetDefault={() => setDefaultMut.mutate(acc.id)}
              onDelete={() => deleteMut.mutate(acc.id)}
              isSettingDefault={settingDefaultId === acc.id}
              isDeleting={deletingId === acc.id}
            />
          ))}
        </div>
      )}

      <AccountForm
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={(form) => createMut.mutate(form)}
        isPending={createMut.isPending}
        isOrg={isOrg}
      />
    </div>
  );
}
